import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Loader2, CheckCircle2, AlertTriangle, FileSignature, Download, FileText, Send } from 'lucide-react';
import { SignatureCanvas, type SignatureCanvasRef } from '@/components/SignatureCanvas';
import { generarContratoPdf } from '@/lib/contratos/contratoPdf';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { getImageUrl } from '@/lib/imageUtils';

type EstadoCarga = 'cargando' | 'listo' | 'firmado' | 'error' | 'caducado';

export default function FirmaContratoPublica() {
  const { token } = useParams<{ token: string }>();
  const [estado, setEstado] = useState<EstadoCarga>('cargando');
  const [mensajeError, setMensajeError] = useState('');
  const [bundle, setBundle] = useState<any>(null);
  const [nombre, setNombre] = useState('');
  const [firma, setFirma] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aceptado, setAceptado] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const sigRef = useRef<SignatureCanvasRef>(null);

  useEffect(() => {
    if (!token) {
      setEstado('error');
      setMensajeError('Token no proporcionado');
      return;
    }
    (async () => {
      try {
        const url = `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/firma-contrato-publico?token=${encodeURIComponent(token!)}`;
        const r = await fetch(url, {
          headers: {
            Authorization: `Bearer ${(import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            apikey: (import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        });
        const payload = await r.json();
        if (!r.ok) {
          if (r.status === 410) { setEstado('caducado'); return; }
          throw new Error(payload?.error || 'No se pudo cargar el contrato');
        }
        setBundle(payload);
        setNombre(payload?.cliente?.nombre_fiscal || payload?.cliente?.nombre || '');
        setSignedUrl(payload?.signed_url || null);
        if (payload?.contrato?.fecha_firma && payload?.contrato?.firma_origen === 'online' && payload?.contrato?.firma_cliente) {
          setEstado('firmado');
        } else {
          setEstado('listo');
        }
      } catch (e: any) {
        setMensajeError(e?.message || 'Enlace no válido');
        setEstado('error');
      }
    })();
  }, [token]);

  // Mapeo de empresa/docConfig recibidos desde el edge function (sin sesión)
  const empresaOverride = useMemo(() => {
    const e = bundle?.empresa;
    if (!e) return null;
    return {
      nombre: e.nombre,
      razon_social: e.razon_social,
      nif_cif: e.nif_cif,
      direccion_calle: e.direccion_calle,
      direccion_numero: e.direccion_numero,
      codigo_postal: e.codigo_postal,
      ciudad: e.ciudad,
      provincia: e.provincia,
      telefono: e.telefono,
      email: e.email,
      logo_url: e.logo_url,
      sello_url: e.sello_url,
      numero_dgp: e.numero_dgp,
      numero_reci: e.numero_reci,
    };
  }, [bundle]);

  const docConfigOverride = useMemo(() => {
    const c = bundle?.docConfig;
    if (!c) return undefined;
    return {
      colorPrimario: c.color_primario || undefined,
      colorSecundario: c.color_secundario || undefined,
      colorAcento: c.color_acento || undefined,
      mostrarLogo: c.mostrar_logo ?? true,
      mostrarDatosEmpresa: c.mostrar_datos_empresa ?? true,
      textoPiePagina: c.texto_pie_pagina || undefined,
      textoLegal: c.texto_legal || undefined,
    };
  }, [bundle]);

  // Generar preview del PDF (sin firma) cuando tengamos los datos
  useEffect(() => {
    if (!bundle || estado !== 'listo') return;
    let cancel = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const { contrato, cliente, sistemas, sedes } = bundle;
        const sedesArr: any[] = sedes || [];
        const sistemasInfo = (sistemas || []).map((s: any) => {
          const sede = s.sede_id ? sedesArr.find((se) => se.id === s.sede_id) : null;
          const direccionPartes = sede ? [sede.direccion, sede.codigo_postal, sede.poblacion, sede.provincia].filter(Boolean).join(', ') : '';
          return {
            tipo: s.tipo,
            tipoLabel: getSystemTypeLabel(s.tipo),
            sedeNombre: sede?.nombre || undefined,
            sedeDireccion: direccionPartes || null,
            ubicacion: s.ubicacion || null,
          };
        });
        const blob = await generarContratoPdf({
          empresaId: contrato.empresa_id,
          cliente,
          contrato,
          sistemas: sistemasInfo,
          empresaOverride,
          docConfigOverride,
        });
        if (cancel) return;
        createdUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdUrl);
      } catch (e) {
        console.error('Error generando preview', e);
      }
    })();
    return () => {
      cancel = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [bundle, estado, empresaOverride, docConfigOverride]);

  const handleFirmar = async () => {
    if (!bundle || !token) return;
    const firmaActual = sigRef.current?.getSignature() || firma;
    if (!firmaActual) {
      alert('Por favor, firma en el recuadro antes de confirmar');
      return;
    }
    if (!nombre.trim()) {
      alert('Por favor, introduce tu nombre como firmante');
      return;
    }
    setEnviando(true);
    try {
      const { contrato, cliente, sistemas, sedes } = bundle;
      const sedesArr: any[] = sedes || [];
      const sistemasInfo = (sistemas || []).map((s: any) => {
        const sede = s.sede_id ? sedesArr.find((se) => se.id === s.sede_id) : null;
        const direccionPartes = sede ? [sede.direccion, sede.codigo_postal, sede.poblacion, sede.provincia].filter(Boolean).join(', ') : '';
        return {
          tipo: s.tipo,
          tipoLabel: getSystemTypeLabel(s.tipo),
          sedeNombre: sede?.nombre || undefined,
          sedeDireccion: direccionPartes || null,
          ubicacion: s.ubicacion || null,
        };
      });
      const blob = await generarContratoPdf({
        empresaId: contrato.empresa_id,
        cliente,
        contrato: {
          ...contrato,
          firma_cliente: firmaActual,
          nombre_firmante_cliente: nombre,
        },
        sistemas: sistemasInfo,
        empresaOverride,
        docConfigOverride,
      });
      const buffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
      const pdfB64 = btoa(binary);

      const url = `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/firma-contrato-publico`;
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          apikey: (import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          token,
          nombre_firmante: nombre,
          firma_base64: firmaActual,
          pdf_base64: pdfB64,
        }),
      });
      const res = await r.json();
      if (!r.ok) throw new Error(res?.error || 'No se pudo enviar la firma');
      if (res?.signed_url) setSignedUrl(res.signed_url);
      setEstado('firmado');
    } catch (e: any) {
      alert(e?.message || 'Error al firmar');
    } finally {
      setEnviando(false);
    }
  };

  const descargarBorrador = () => {
    if (!previewUrl || !bundle) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `Borrador-${bundle?.contrato?.numero_formateado || 'contrato'}.pdf`;
    a.click();
  };

  const clausulas = useMemo(() => {
    const c = bundle?.contrato?.clausulas;
    if (Array.isArray(c)) return c as any[];
    return [];
  }, [bundle]);

  if (estado === 'cargando') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (estado === 'error' || estado === 'caducado') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {estado === 'caducado' ? 'Enlace caducado' : 'Enlace no válido'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {estado === 'caducado'
                ? 'Este enlace de firma ha caducado. Solicita uno nuevo a la empresa.'
                : mensajeError || 'No hemos podido cargar este contrato. Comprueba el enlace o contacta con la empresa.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (estado === 'firmado') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Contrato firmado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <StatusBar paso="firmado" />
            <p>El contrato <strong>{bundle?.contrato?.numero_formateado}</strong> ha sido firmado correctamente.</p>
            {signedUrl ? (
              <Button asChild className="w-full" size="lg">
                <a href={signedUrl} target="_blank" rel="noopener noreferrer" download>
                  <Download className="h-4 w-4 mr-2" /> Descargar mi copia firmada
                </a>
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">La empresa te hará llegar una copia firmada.</p>
            )}
            <p className="text-xs text-muted-foreground">Puedes cerrar esta ventana cuando termines.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const c = bundle?.contrato;
  const emp = bundle?.empresa;
  return (
    <div className="min-h-screen bg-muted/30 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {emp?.logo_url && (
          <div className="flex flex-col items-center gap-2">
            <img src={getImageUrl(emp.logo_url) || emp.logo_url} alt={emp.nombre} className="h-20 object-contain" />
            {(emp?.razon_social || emp?.nombre) && (
              <div className="text-sm text-muted-foreground font-medium">{emp.razon_social || emp.nombre}</div>
            )}
          </div>
        )}

        <StatusBar paso="pendiente_firma" />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              Contrato {c?.numero_formateado}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-muted-foreground">Tipo:</span> {c?.tipo === 'seguridad' ? 'Seguridad' : 'PCI'}</div>
              <div><span className="text-muted-foreground">Duración:</span> {c?.duracion_meses} meses</div>
              <div><span className="text-muted-foreground">Inicio:</span> {new Date(c?.fecha_inicio).toLocaleDateString('es-ES')}</div>
              <div><span className="text-muted-foreground">Fin:</span> {new Date(c?.fecha_fin).toLocaleDateString('es-ES')}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Importe anual:</span> <strong>{Number(c?.precio_total).toFixed(2)} €</strong> {c?.iva_incluido ? '(IVA incluido)' : '(+ IVA)'}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Pago {c?.periodicidad_pago}:</span> {Number(c?.precio_periodo).toFixed(2)} €</div>
            </div>
          </CardContent>
        </Card>

        {/* Vista previa del PDF */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Vista previa del contrato
            </CardTitle>
            <Button variant="outline" size="sm" onClick={descargarBorrador} disabled={!previewUrl}>
              <Download className="h-3.5 w-3.5 mr-1" /> Descargar borrador
            </Button>
          </CardHeader>
          <CardContent>
            {previewUrl ? (
              <iframe
                src={previewUrl}
                className="w-full rounded border bg-background"
                style={{ height: 600 }}
                title="Vista previa del contrato"
              />
            ) : (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Generando vista previa…
              </div>
            )}
          </CardContent>
        </Card>

        {/* Secciones legales en accordion */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Condiciones del contrato</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              {c?.objeto && (
                <AccordionItem value="objeto">
                  <AccordionTrigger>Objeto del contrato</AccordionTrigger>
                  <AccordionContent>
                    <p className="whitespace-pre-wrap text-sm">{c.objeto}</p>
                  </AccordionContent>
                </AccordionItem>
              )}
              {clausulas.length > 0 && (
                <AccordionItem value="clausulas">
                  <AccordionTrigger>Cláusulas ({clausulas.length})</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      {clausulas.map((cl: any, i: number) => (
                        <div key={i} className="text-sm">
                          {cl.titulo && <div className="font-semibold mb-1">{cl.titulo}</div>}
                          <p className="whitespace-pre-wrap text-muted-foreground">{cl.contenido || cl.texto || ''}</p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
              {c?.texto_legal && (
                <AccordionItem value="legal">
                  <AccordionTrigger>Texto legal</AccordionTrigger>
                  <AccordionContent>
                    <p className="whitespace-pre-wrap text-sm">{c.texto_legal}</p>
                  </AccordionContent>
                </AccordionItem>
              )}
              {c?.observaciones && (
                <AccordionItem value="obs">
                  <AccordionTrigger>Observaciones</AccordionTrigger>
                  <AccordionContent>
                    <p className="whitespace-pre-wrap text-sm">{c.observaciones}</p>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Firma</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre y apellidos del firmante</Label>
              <Input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre completo" />
            </div>
            <SignatureCanvas ref={sigRef} title="Firma con el dedo o el ratón" onSignatureChange={setFirma} />
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={aceptado} onCheckedChange={(v) => setAceptado(v === true)} className="mt-0.5" />
              <span>He revisado el contrato y acepto sus condiciones, cláusulas y precio.</span>
            </label>
            <Button onClick={handleFirmar} disabled={enviando || !aceptado} className="w-full" size="lg">
              {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSignature className="h-4 w-4 mr-2" />}
              {enviando ? 'Firmando…' : 'Firmar y enviar'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatusBar({ paso }: { paso: 'pendiente_firma' | 'firmado' }) {
  const pasos = [
    { key: 'generado', label: 'Generado', done: true },
    { key: 'enviado', label: 'Enviado', done: true },
    { key: 'firmado', label: 'Firmado', done: paso === 'firmado' },
  ];
  return (
    <div className="flex items-center justify-center gap-2 text-xs sm:text-sm flex-wrap">
      {pasos.map((p, i) => (
        <div key={p.key} className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${p.done ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted text-muted-foreground'}`}>
            {p.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-2 w-2 rounded-full bg-current opacity-50" />}
            {p.label}
          </div>
          {i < pasos.length - 1 && <div className="w-6 h-px bg-border" />}
        </div>
      ))}
    </div>
  );
}
